import { ENTITY } from "@equidefi/constants/profile";
import {
  INQUIRY_APPROVED_STATUSES,
  INQUIRY_DECLINED,
  INQUIRY_EXPIRED,
  INQUIRY_FAILED,
  INQUIRY_FOR_REVIEW,
} from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types/dto";
import { Icon } from "@equidefi/ui/icon";
import type React from "react";
import { useMemo } from "react";

import { VAULT_ROUTES } from "@/constants/routes";

import { InvestmentStepCard } from "./InvestmentStepCard";

interface IdentityStepCardProps {
  investment: DTO.Investment;
  vaultRecords: DTO.InvestmentVaultRecord[] | undefined;
}

/**
 * Status of every identity check this investment requires: one KYC per vault record flagged `is_kyc`,
 * plus the entity's KYB, which is linked to the investment's vault. `undefined` means not started.
 */
const requiredCheckStatuses = (investment: DTO.Investment, vaultRecords: DTO.InvestmentVaultRecord[] | undefined) => {
  const kycRecords = (vaultRecords ?? []).filter((record) => record.is_kyc);
  const statuses = kycRecords.map((record) => record.inquiry?.status);

  if (investment.vault?.entity_type === ENTITY) {
    statuses.push(investment.vault.inquiry?.status);
  }

  return statuses;
};

export const IdentityStepCard: React.FC<IdentityStepCardProps> = ({ investment, vaultRecords }) => {
  const to = VAULT_ROUTES.inquiry(investment.id);
  const statuses = useMemo(() => requiredCheckStatuses(investment, vaultRecords), [investment, vaultRecords]);
  const hasStatus = (matches: readonly string[]) => statuses.some((status) => !!status && matches.includes(status));

  if (hasStatus([INQUIRY_DECLINED, INQUIRY_FAILED])) {
    return (
      <InvestmentStepCard
        scheme="red"
        label="DECLINED"
        heading="Identity Check Declined"
        text="One or more Identity Checks were declined. View details for next steps."
        icon={Icon.Search}
        to={to}
      />
    );
  }

  if (hasStatus([INQUIRY_EXPIRED])) {
    return (
      <InvestmentStepCard
        scheme="red"
        label="EXPIRED"
        heading="Identity Check Expired"
        text="One or more Identity Checks expired before they were completed."
        icon={Icon.Search}
        to={to}
      />
    );
  }

  if (statuses.length > 0 && statuses.every((status) => INQUIRY_APPROVED_STATUSES.some((s) => s === status))) {
    return (
      <InvestmentStepCard
        scheme="green"
        label="APPROVED"
        heading="Identity Check Approved"
        text="All required Identity Checks have been approved."
        icon={Icon.Search}
        to={to}
      />
    );
  }

  if (hasStatus([INQUIRY_FOR_REVIEW])) {
    return (
      <InvestmentStepCard
        scheme="blue"
        label="IN REVIEW"
        heading="Identity Check In Review"
        text="Your Identity Checks are being reviewed by the issuer."
        icon={Icon.Search}
        to={to}
      />
    );
  }

  return (
    <InvestmentStepCard
      scheme="yellow"
      label="PENDING"
      heading="Identity Check Pending"
      text="Your Identity Checks are still being reviewed."
      icon={Icon.Search}
      to={to}
    />
  );
};

export default IdentityStepCard;
